import * as React from 'react';
import { ListFilter, X } from 'lucide-react';
import { Badge } from '../../primitives/badge.js';
import { Button } from '../../primitives/button.js';
import { Label } from '../../primitives/label.js';
import { Popover, PopoverContent, PopoverTrigger } from '../../primitives/popover.js';
import { Separator } from '../../primitives/separator.js';
import { FilterControl } from './filter-control.js';
import { formatValueText } from '../format.js';
import { isFilterApplied, isMultiValue, isRangeValue } from '../list-state.js';
import type { FilterValue, ListState, ResourceFilter } from '../types.js';

export interface FilterBarProps {
  filters: readonly ResourceFilter[];
  state: ListState;
  onChange: (patch: Partial<ListState>) => void;
}

function summarise(filter: ResourceFilter, value: FilterValue): string {
  if (isMultiValue(value)) {
    if (filter.type === 'enum') {
      const labels = value
        .map((item) => filter.options.find((option) => option.value === item)?.label ?? item)
        .slice(0, 2);
      return value.length > 2
        ? `${labels.join(', ')} +${String(value.length - 2)}`
        : labels.join(', ');
    }
    return value.join(', ');
  }
  if (isRangeValue(value)) {
    return [value.from, value.to].filter(Boolean).join(' → ');
  }
  if (typeof value === 'boolean') {
    if (filter.type === 'boolean')
      return value ? (filter.trueLabel ?? 'Sì') : (filter.falseLabel ?? 'No');
    return value ? 'Sì' : 'No';
  }
  if (filter.type === 'enum' && typeof value === 'string') {
    return filter.options.find((option) => option.value === value)?.label ?? value;
  }
  return formatValueText(value);
}

/**
 * Applied filters are chips, unapplied ones live behind a single "Filtri"
 * popover. The chip row is the important half: it is the only honest answer to
 * "why am I not seeing the record I expect".
 */
export function FilterBar({ filters, state, onChange }: FilterBarProps) {
  const [open, setOpen] = React.useState(false);

  const setFilter = (id: string, value: FilterValue) => {
    onChange({ filters: { ...state.filters, [id]: value } });
  };

  const applied = filters.filter((filter) => isFilterApplied(state.filters[filter.id] ?? null));

  return (
    <div data-slot="filter-bar" className="flex flex-wrap items-center gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="secondary" size="sm">
            <ListFilter />
            Filtri
            {applied.length > 0 && (
              <Badge tone="accent" className="ml-1">
                {applied.length}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-0">
          <div className="grid max-h-[28rem] gap-4 overflow-y-auto p-4">
            {filters.map((filter) => (
              <div key={filter.id} className="grid gap-1.5">
                <Label htmlFor={`filter-${filter.id}`}>{filter.label}</Label>
                <FilterControl
                  id={`filter-${filter.id}`}
                  filter={filter}
                  value={state.filters[filter.id] ?? null}
                  onChange={(value) => setFilter(filter.id, value)}
                />
              </div>
            ))}
          </div>
          {applied.length > 0 && (
            <>
              <Separator />
              <div className="p-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  onClick={() => onChange({ filters: {} })}
                >
                  Azzera tutti i filtri
                </Button>
              </div>
            </>
          )}
        </PopoverContent>
      </Popover>

      {applied.map((filter) => (
        <Badge key={filter.id} tone="accent" className="gap-1 pr-1 normal-case tracking-normal">
          <span className="text-faint">{filter.label}:</span>
          {summarise(filter, state.filters[filter.id] ?? null)}
          <button
            type="button"
            aria-label={`Rimuovi filtro ${filter.label}`}
            className="rounded-pill p-0.5 transition-colors hover:bg-accent-soft-ink/10"
            onClick={() => setFilter(filter.id, null)}
          >
            <X className="size-3" />
          </button>
        </Badge>
      ))}
    </div>
  );
}
